import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Calendar } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";

const links = [
  ["/", "nav.home"], ["/chambres-hotes", "nav.chambres"], ["/gites", "nav.gites"],
  ["/nos-sejours", "nav.sejours"], ["/seminaires", "nav.seminaires"], ["/le-domaine", "nav.domaine"], ["/la-region", "nav.region"],
  ["/contact", "nav.contact"],
];

export default function Navbar() {
  const { t, lang, changeLang } = useI18n();
  const { settings } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = loc.pathname === "/";
  const solid = scrolled || !isHome;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-[0_4px_24px_rgba(44,48,48,0.05)]" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-28">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
            <img
              src={solid ? "/header-logo-blue.png?v=4" : "/footer-logo.png?v=2"}
              alt="Domaine de la Bégaudière"
              className="h-14 lg:h-20 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1.5">
            {links.map(([to, key]) => {
              const active = to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={`nav-link-${key.split(".")[1]}`}
                  className={`whitespace-nowrap px-2.5 xl:px-3 py-2 text-[13px] xl:text-sm font-body font-medium tracking-tight transition-colors rounded-full ${
                    solid ? "text-ink-600 hover:text-brand" : "text-white/90 hover:text-white"
                  } ${active ? (solid ? "text-brand" : "text-white") : ""}`}
                >
                  {t(key)}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 lg:gap-3">
            <div className={`hidden sm:flex items-center text-xs font-medium rounded-full overflow-hidden border ${solid ? "border-ink-400/30" : "border-white/40"}`}>
              {["fr", "en"].map((l) => (
                <button
                  key={l}
                  onClick={() => changeLang(l)}
                  data-testid={`lang-switch-${l}`}
                  className={`px-2.5 py-1 uppercase transition-colors ${
                    lang === l ? "bg-brand text-white" : solid ? "text-ink-600" : "text-white"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>

            <Link
              to="/disponibilites"
              data-testid="nav-reserver-btn"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand text-white ring-2 ring-white/90 px-5 py-2.5 text-sm font-medium hover:bg-brand-600 transition-colors hover:scale-[1.02] active:scale-[0.98]"
            >
              <Calendar size={16} strokeWidth={1.75} />
              {t("nav.reserver")}
            </Link>

            <Sheet>
              <SheetTrigger asChild>
                <button
                  className={`lg:hidden p-2 rounded-full ${solid ? "text-ink" : "text-white"}`}
                  data-testid="mobile-menu-trigger"
                  aria-label="Menu"
                >
                  <Menu size={26} strokeWidth={1.75} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-cream w-[86%] max-w-sm p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-black/5">
                    <img src="/header-logo-blue.png?v=4" alt="Domaine de la Bégaudière" className="h-16 w-auto" />
                  </div>
                  <nav className="flex-1 overflow-y-auto p-4">
                    {links.map(([to, key]) => {
                      const active = to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to);
                      return (
                        <SheetClose asChild key={to}>
                          <Link
                            to={to}
                            data-testid={`mobile-nav-${key.split(".")[1]}`}
                            className={`block px-4 py-3.5 rounded-xl font-body text-lg transition-colors ${
                              active ? "bg-brand/10 text-brand" : "text-ink hover:bg-black/5"
                            }`}
                          >
                            {t(key)}
                          </Link>
                        </SheetClose>
                      );
                    })}
                  </nav>
                  <div className="p-4 border-t border-black/5 space-y-3">
                    <div className="flex items-center justify-center gap-2">
                      {["fr", "en"].map((l) => (
                        <button
                          key={l}
                          onClick={() => changeLang(l)}
                          className={`px-4 py-1.5 rounded-full text-sm uppercase font-medium border ${
                            lang === l ? "bg-brand text-white border-brand" : "border-ink-400/40 text-ink-600"
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                    <SheetClose asChild>
                      <Link
                        to="/disponibilites"
                        className="block text-center rounded-full bg-brand text-white px-6 py-3.5 font-medium"
                        data-testid="mobile-reserver-btn"
                      >
                        {t("nav.reserver")}
                      </Link>
                    </SheetClose>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
